import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  HbrSelect,
  HbrOption,
  HbrTag,
  HbrForm,
  HbrInput,
} from '@harbor/elements-react-standalone';
import { moodleFetch, useMoodleSpeakers } from '../../../hooks/useMoodleEventBooking';
import { selectMoodleSpeakersMap } from '../../../support/slices/moodleSlice';
import { MoodleSpeaker } from '../../../types/moodleEventBooking';
import StbApiModal from '../../security-toolbox-ui/StbApiModal';
import StbButton from '../../security-toolbox-ui/StbButton';
import { renderErrorMessage } from '../../security-toolbox-ui/security-toolbox-ui-utils';
import { asSingleValue } from '../../../support/utils';


type MoodleSpeakersSelectProps = {
  selectedSpeakerIds?: number[];
  onChange?: (selected: MoodleSpeaker[]) => void;
  label?: string;
};

export function MoodleSpeakersSelect({
  selectedSpeakerIds = [],
  onChange,
  label = 'Select Speakers',
}: MoodleSpeakersSelectProps) {
  const speakerMap = useSelector(selectMoodleSpeakersMap);
  const speakers: MoodleSpeaker[] = useMemo(
    () => Object.values(speakerMap).filter((s) => !!s.name && s.name.trim().length > 0),
    [speakerMap]
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<MoodleSpeaker>>({});
  const [nameTouched, setNameTouched] = useState(false);

  const selected = useMemo(
    () => speakers.filter(s => selectedSpeakerIds.includes(s.id)),
    [selectedSpeakerIds, speakers]
  );

  const availableOptions = useMemo(
    () => speakers.filter(s => !selectedSpeakerIds.includes(s.id)),
    [speakers, selectedSpeakerIds]
  );

  const optionList = useMemo(() => {
    const all = [...selected, ...availableOptions];
    return all.filter((sp, idx, arr) => arr.findIndex(item => item.id === sp.id) === idx);
  }, [selected, availableOptions]);

  const { refetch } = useMoodleSpeakers();

  const emitSelection = (ids: number[]) => {
    const newSpeakers = speakers.filter(s => ids.includes(s.id));
    onChange?.(newSpeakers);
  };

  const handleSelectChange = (e: any) => {
    const detail = e.detail;
    // Preferred: Harbor emits all selected values in detail.values (string[]).
    if (Array.isArray(detail?.values)) {
      const values: string[] = detail.values;
      if (values.includes('__add__')) {
        setModalOpen(true);
        return;
      }
      emitSelection(values.map(Number));
      return;
    }

    // Fallback: only last value present in detail.value — toggle against current selection.
    const last = detail?.value ?? (e.target?.value ?? '');
    if (!last) return;

    if (last === '__add__') {
      setModalOpen(true);
      return;
    }

    const lastId = Number(last);
    const exists = selectedSpeakerIds.includes(lastId);
    const nextIds = exists
      ? selectedSpeakerIds.filter(id => id !== lastId)
      : [...selectedSpeakerIds, lastId];

    emitSelection(nextIds);
  };

  const handleTagRemove = (removeId: number) => {
    const nextIds = selectedSpeakerIds.filter(id => id !== removeId);
    emitSelection(nextIds);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    const result = await moodleFetch<MoodleSpeaker>('speakers/', {}, {
      method: 'POST',
      body: JSON.stringify(form),
    });
    refetch?.();
  };

  return (
    <div>
      <div className="flex flex-row items-center gap-4">
        <HbrSelect
          label={label}
          multiple
          value={selectedSpeakerIds.map(String)}
          placeholder="Add speaker..."
          onHbr-change={handleSelectChange}
          className="flex-1 w-full"
        >
          {optionList.map(sp => (
            <HbrOption key={sp.id} value={String(sp.id)}>
              {sp.name}
            </HbrOption>
          ))}
          <HbrOption value="__add__">+ Add Speaker</HbrOption>
        </HbrSelect>

        <StbButton
          onClick={() => setModalOpen(true)}
          role="add"
          variant="text"
          size="medium"
          className="mt-3"
        >
          Add Speaker
        </StbButton>
      </div>

      <div style={{ margin: '8px 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {selected.map(sp => (
          <HbrTag
            key={sp.id}
            removable
            onHbr-remove={() => handleTagRemove(sp.id)}
          >
            {sp.name}
          </HbrTag>
        ))}
      </div>

      <StbApiModal
        open={modalOpen}
        onClose={async () => {
          setModalOpen(false)
          setForm({});
          setNameTouched(false);
        }}
        title="Add Speaker"
        role="save"
        onSubmit={handleSubmit}
        submitDisabled={!form.name || form.name.trim() === ''}
        submitLabel="Add"
        submitSuccess="Speaker added"
        submitError="Error adding speaker"
      >
        <HbrForm>
          <HbrInput
            required
            name="name"
            value={form.name ?? ''}
            onHbr-input={(e) => {
              setForm(prev => ({ ...prev, url: asSingleValue(e.target.value) }));
              setNameTouched(true);
            }}
          >
            <div slot="label">Name <em className="text-gray-500">(required)</em></div>
            {renderErrorMessage(nameTouched && !form.name, 'Please enter a name')}
          </HbrInput>
          <HbrInput name="title" label="Title" value={form.title ?? ''} onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))} />
        </HbrForm>
      </StbApiModal>
    </div>
  );
}